import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Compass, Target, Layers, Briefcase, Rocket } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/site/Reveal";
import Marquee from "@/components/site/Marquee";
import SectionHead from "@/components/site/SectionHead";
import FAQ from "@/components/site/FAQ";
import CTASection from "@/components/site/CTASection";
import { useLang } from "@/i18n";

const PILLAR_ICONS = [Compass, Target, Layers, Briefcase, Rocket];
const HERO_IMG = "/images/cristina-meeting.webp";
const TEASER_PHOTOS = [
  { src: "/images/cristina-meeting.webp", alt: "Cristina presenta il progetto Tykun X al team in sala riunioni" },
  { src: "/images/cristina-team.webp", alt: "Cristina al lavoro con il team sui materiali di progetto" },
  { src: "/images/cristina-tykun.webp", alt: "Cristina Angelini durante il lavoro sul progetto Tykun" },
  { src: "/images/cristina-blazer.webp", alt: "Ritratto di Cristina Angelini" },
];

const Home = () => {
  const { d } = useLang();
  const h = d.home;

  const scrollToMethod = () => {
    const el = document.getElementById("metodo");
    if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -80 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };

  const caseImages = ["/images/monaco-harbour.webp", "/images/sanya-cover.png"];

  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden bg-paper" data-testid="hero-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 pb-20 pt-16 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-12 lg:pb-28 lg:pt-24">
          <div className="relative z-10 lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-accent"
              data-testid="hero-overline"
            >
              <span className="h-px w-10 bg-accent" />
              {h.heroOverline}
            </motion.p>
            <h1 className="font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="hero-headline">
              <MaskedLine delay={0.1}>{h.heroL1}</MaskedLine>
              <MaskedLine delay={0.25}>{h.heroL2}</MaskedLine>
              <MaskedLine delay={0.4}>
                {h.heroL3a} <span className="italic text-accent">{h.heroL3b}</span>
              </MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-8 max-w-xl text-base leading-relaxed text-mutedink sm:text-lg"
              data-testid="hero-subheadline"
            >
              {h.heroSub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-10 flex flex-col gap-4 sm:flex-row"
            >
              <Link
                to="/contatti"
                data-testid="hero-cta-parliamone-button"
                className="inline-flex items-center justify-center gap-3 rounded-md bg-accent px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-accent-dark"
              >
                {h.ctaPrimary}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                type="button"
                onClick={scrollToMethod}
                data-testid="hero-secondary-cta-button"
                className="inline-flex items-center justify-center gap-3 rounded-md border border-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-ink hover:text-white"
              >
                {h.ctaSecondary}
              </button>
            </motion.div>
          </div>
          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative h-72 overflow-hidden rounded-md sm:h-96 lg:h-[520px]"
            >
              <img src={HERO_IMG} alt={h.heroImgAlt} className="h-full w-full object-cover" data-testid="hero-image" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="absolute -bottom-6 -left-4 rounded-md border border-ink/10 bg-white p-5 shadow-sm sm:-left-8"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mutedink">{h.badgeTop}</p>
              <p className="mt-1 font-serif text-lg text-ink">{h.badgeBottom}</p>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee items={d.marquee} />

      <section id="problema" className="bg-paper py-24 lg:py-32" data-testid="problema-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={h.problema.overline} title={h.problema.title} />
          <Reveal delay={0.1} className="mt-10 grid gap-10 lg:grid-cols-12">
            <div className="space-y-2 font-serif text-xl leading-relaxed text-ink/85 sm:text-2xl lg:col-span-7">
              <p>{h.problema.line1}</p>
              <p>{h.problema.line2}</p>
              <p className="pt-4 font-sans text-base text-mutedink sm:text-lg">{h.problema.closing}</p>
            </div>
            <div className="hidden lg:col-span-5 lg:block">
              <img src="/images/tykun-team.webp" alt={h.problema.imgAlt} loading="lazy" className="h-full w-full rounded-md object-cover" data-testid="problema-image" />
            </div>
          </Reveal>
          <div className="mt-16 grid gap-px overflow-hidden rounded-md bg-ink/10 md:grid-cols-3" data-testid="problema-cards">
            {h.problema.cards.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.12} className="group bg-paper p-8 transition-colors duration-300 hover:bg-white lg:p-10">
                <p className="font-mono text-sm text-accent">{p.n}</p>
                <h3 className="mt-4 font-serif text-2xl uppercase tracking-wide text-ink">{p.title}</h3>
                <p className="mt-4 font-medium leading-relaxed text-ink">{p.lead}</p>
                <p className="mt-3 text-sm leading-relaxed text-mutedink">{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="hero-grain relative bg-ink py-24 text-paper lg:py-32" data-testid="perche-io-section">
        <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHead dark overline={h.perche.overline} title={h.perche.title} />
              <Reveal delay={0.15} className="mt-8 space-y-4 text-base leading-relaxed text-white/65">
                <p>{h.perche.p1}</p>
                <p>{h.perche.p2}</p>
                <p>
                  {h.perche.p3a} <span className="text-paper">{h.perche.p3b}</span>
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <div className="divide-y divide-white/10 border-y border-white/10">
                {h.perche.pillars.map((p, i) => {
                  const Icon = PILLAR_ICONS[i];
                  return (
                    <Reveal key={p.title} delay={i * 0.08} className="group flex items-start gap-6 py-6 transition-colors duration-300 hover:bg-white/[0.03] sm:items-center">
                      <Icon className="mt-1 h-6 w-6 shrink-0 text-accent-bright sm:mt-0" strokeWidth={1.5} />
                      <div className="flex flex-1 flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-6">
                        <h3 className="w-56 shrink-0 font-mono text-sm font-medium uppercase tracking-[0.2em] text-paper">{p.title}</h3>
                        <p className="text-sm leading-relaxed text-white/60">{p.body}</p>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-32" data-testid="differenza-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={h.diff.overline} title={h.diff.title} lead={h.diff.lead} />
          <div className="mt-16 grid gap-px overflow-hidden rounded-md bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
            {h.diff.items.map((it, i) => (
              <Reveal key={it.t} delay={i * 0.08} className={`p-8 lg:p-10 ${i === h.diff.items.length - 1 ? "bg-ink text-paper" : "bg-white"}`} data-testid={`diff-item-${i}`}>
                <p className={`font-serif text-4xl font-semibold ${i === h.diff.items.length - 1 ? "text-accent-bright" : "text-outline"}`}>{String(i + 1).padStart(2, "0")}</p>
                <h3 className={`mt-5 font-serif text-2xl ${i === h.diff.items.length - 1 ? "text-paper" : "text-ink"}`}>{it.t}</h3>
                <p className={`mt-4 text-sm leading-relaxed ${i === h.diff.items.length - 1 ? "text-white/70" : "text-mutedink"}`}>{it.b}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="metodo" className="bg-paper py-24 lg:py-32" data-testid="metodo-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={h.metodo.overline} title={h.metodo.title} lead={h.metodo.lead} />
          <div className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-md bg-ink/10 lg:grid-cols-7" data-testid="metodo-steps">
            {h.metodo.steps.map((s, i) => (
              <Reveal key={s} delay={i * 0.08} className="group relative bg-paper p-7 transition-colors duration-300 hover:bg-ink">
                <p className="font-serif text-5xl text-outline transition-colors duration-300 group-hover:text-accent group-hover:[-webkit-text-stroke:0]">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-6 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 group-hover:text-paper">
                  {s}
                </h3>
                {i < h.metodo.steps.length - 1 && (
                  <ArrowRight className="absolute right-4 top-7 h-4 w-4 text-mutedink/40 transition-colors duration-300 group-hover:text-accent" />
                )}
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-12 max-w-3xl">
            <p className="font-serif text-2xl leading-snug text-ink sm:text-3xl">
              {h.metodo.punchA} <span className="italic text-accent">{h.metodo.punchB}</span>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-32" data-testid="modalita-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={h.modalita.overline} title={h.modalita.title} />
          <div className="mt-14 grid gap-8 lg:grid-cols-3" data-testid="mode-cards">
            {h.modalita.cards.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.1} className="flex flex-col rounded-md border border-ink/12 bg-paper p-8 transition-all duration-300 hover:-translate-y-1 hover:border-accent lg:p-10">
                <p className="font-serif text-6xl text-outline">{m.n}</p>
                <h3 className="mt-6 font-serif text-2xl text-ink">{m.t}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-mutedink">{m.b}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10">
            <Link to="/contatti" data-testid="modalita-cta-button" className="inline-flex items-center gap-3 rounded-md bg-accent px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-accent-dark">
              {h.modalita.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-paper py-24 lg:py-32" data-testid="due-aree-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={h.aree.overline} title={h.aree.title} lead={h.aree.lead} />
          <div className="mt-16 grid gap-8 lg:grid-cols-2">
            <Reveal className="flex flex-col rounded-md border border-ink/12 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-accent lg:p-12" data-testid="area-card-marketing">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{h.aree.area1.label}</p>
              <h3 className="mt-4 font-serif text-3xl text-ink sm:text-4xl">{h.aree.area1.title}</h3>
              <p className="mt-4 text-sm font-medium uppercase tracking-[0.12em] text-mutedink">{h.aree.area1.forWho}</p>
              <ul className="mt-4 flex-1 space-y-2.5 text-base leading-relaxed text-ink/80">
                {h.aree.area1.items.map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="mt-2.5 h-1 w-1 shrink-0 rotate-45 bg-accent" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/marketing-branding"
                data-testid="area-cta-marketing"
                className="mt-10 inline-flex items-center gap-3 self-start rounded-md bg-accent px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-accent-dark"
              >
                {h.aree.area1.cta}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
            <Reveal delay={0.15} className="flex flex-col rounded-md bg-ink p-8 text-paper transition-all duration-300 hover:-translate-y-1 lg:p-12" data-testid="area-card-fiere">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent-bright">{h.aree.area2.label}</p>
              <h3 className="mt-4 font-serif text-3xl sm:text-4xl">{h.aree.area2.title}</h3>
              <p className="mt-4 text-sm font-medium uppercase tracking-[0.12em] text-white/50">{h.aree.area2.forWho}</p>
              <ul className="mt-4 flex-1 space-y-2.5 text-base leading-relaxed text-white/80">
                {h.aree.area2.items.map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="mt-2.5 h-1 w-1 shrink-0 rotate-45 bg-accent" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-8 border-l-2 border-accent pl-4 font-serif text-lg italic leading-snug text-white/90">{h.aree.area2.quote}</p>
              <Link
                to="/fiere-eventi-lanci"
                data-testid="area-cta-fiere"
                className="mt-10 inline-flex items-center gap-3 self-start rounded-md bg-white px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-accent hover:text-white"
              >
                {h.aree.area2.cta}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-32" data-testid="packages-preview-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <SectionHead overline={h.packPrev.overline} title={h.packPrev.title} lead={h.packPrev.lead} />
            <Reveal delay={0.15}>
              <Link to="/marketing-branding" data-testid="packages-preview-link" className="inline-flex items-center gap-2 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:text-accent">
                {h.packPrev.cta} <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-px overflow-hidden rounded-md bg-ink/10 md:grid-cols-3" data-testid="packages-preview-grid">
            {d.mb.packages.list.map((pkg, i) => (
              <Reveal key={pkg.name} delay={i * 0.08} className="flex flex-col bg-white p-8 transition-colors duration-300 hover:bg-paper lg:p-10" data-testid={`pack-prev-${pkg.name.toLowerCase()}`}>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{pkg.name}</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink">{pkg.role}</p>
                <p className="mt-5 whitespace-nowrap font-serif text-3xl tracking-tight text-ink">
                  {pkg.price} <span className="font-sans text-xs font-normal text-mutedink">{pkg.vat}</span>
                </p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink">{pkg.duration}</p>
                <p className="mt-5 flex-1 text-sm leading-relaxed text-mutedink">{pkg.problem}</p>
                <Link
                  to="/marketing-branding"
                  data-testid={`pack-prev-${pkg.name.toLowerCase()}-cta`}
                  className="mt-7 inline-flex items-center gap-2 self-start py-2 text-xs font-semibold uppercase tracking-[0.15em] text-accent transition-colors hover:text-accent-dark"
                >
                  {h.packPrev.cta} <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-paper py-24 lg:py-32" data-testid="fiere-preview-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <SectionHead overline={h.fierePrev.overline} title={h.fierePrev.title} lead={h.fierePrev.lead} />
            <Reveal delay={0.15}>
              <Link to="/fiere-eventi-lanci" data-testid="fiere-preview-link" className="inline-flex items-center gap-2 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:text-accent">
                {h.fierePrev.cta} <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-px overflow-hidden rounded-md bg-ink/10 md:grid-cols-3" data-testid="fiere-preview-grid">
            {d.fierePage.packages.list.map((pkg, i) => (
              <Reveal key={pkg.name} delay={i * 0.08} className="flex flex-col bg-paper p-8 transition-colors duration-300 hover:bg-white lg:p-10" data-testid={`fiere-prev-${i}`}>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{pkg.name}</p>
                <p className="mt-5 whitespace-nowrap font-serif text-3xl tracking-tight text-ink">
                  {pkg.price} <span className="font-sans text-xs font-normal text-mutedink">{pkg.vat}</span>
                </p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink">{pkg.duration}</p>
                <p className="mt-5 flex-1 text-sm leading-relaxed text-mutedink">{pkg.problem}</p>
                <Link
                  to="/fiere-eventi-lanci"
                  data-testid={`fiere-prev-${i}-cta`}
                  className="mt-7 inline-flex items-center gap-2 self-start py-2 text-xs font-semibold uppercase tracking-[0.15em] text-accent transition-colors hover:text-accent-dark"
                >
                  {h.fierePrev.cta} <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-32" data-testid="case-preview-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <SectionHead overline={h.casePrev.overline} title={h.casePrev.title} />
            <Reveal delay={0.15}>
              <Link to="/case-study" data-testid="case-preview-all-link" className="inline-flex items-center gap-2 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:text-accent">
                {h.casePrev.allLink} <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-8 lg:grid-cols-2">
            {h.casePrev.cards.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.12}>
                <Link to="/case-study" className="group block" data-testid={`case-preview-card-${i}`}>
                  <div className="relative overflow-hidden rounded-t-md">
                    <img src={caseImages[i]} alt={c.imgAlt} loading="lazy" className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    {c.badge && (
                      <span className="absolute left-4 top-4 rounded-sm bg-accent px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                        {c.badge}
                      </span>
                    )}
                  </div>
                  <div className="rounded-b-md border border-t-0 border-ink/10 bg-white p-7">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent">{c.tag}</p>
                    <h3 className="mt-2 font-serif text-2xl text-ink transition-colors group-hover:text-accent">{c.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-mutedink">{c.body}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-paper py-24 lg:py-32" data-testid="chi-sono-teaser-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 sm:px-8 lg:grid-cols-12 lg:px-12">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <img src="/images/cristina-desk.webp" alt={h.teaser.imgAlt} loading="lazy" className="aspect-[4/5] w-full rounded-md object-cover" data-testid="chi-sono-teaser-image" />
              <div className="absolute -bottom-5 -right-4 rounded-md bg-ink px-6 py-4 sm:-right-6">
                <p className="font-serif text-xl text-paper">Cristina Angelini</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent-bright">Marketing Facile</p>
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-7 lg:pl-8">
            <SectionHead overline={h.teaser.overline} title={h.teaser.title} />
            <Reveal delay={0.15} className="mt-8 space-y-4 text-base leading-relaxed text-mutedink sm:text-lg">
              <p>{h.teaser.p1}</p>
              <p>
                {h.teaser.p2a} <span className="text-ink">{h.teaser.p2b}</span>.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <Link to="/chi-sono" data-testid="chi-sono-teaser-link" className="mt-8 inline-flex items-center gap-3 rounded-md border border-ink px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-ink hover:text-white">
                {h.teaser.cta} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="min-w-0 lg:col-span-12">
            <div className="-mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-4 sm:-mx-8 sm:px-8 lg:-mx-12 lg:px-12" data-testid="chi-sono-teaser-gallery">
              {TEASER_PHOTOS.map((g, i) => (
                <div key={g.src} className="group w-72 shrink-0 snap-center overflow-hidden rounded-md sm:w-96">
                  <img src={g.src} alt={g.alt} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" data-testid={`chi-sono-teaser-gallery-${i}`} />
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-white py-24 lg:py-32" data-testid="servizi-preview-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={h.serviziPrev.overline} title={h.serviziPrev.title} lead={h.serviziPrev.lead} />
          <div className="mt-12 grid gap-px overflow-hidden rounded-md bg-ink/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="servizi-preview-grid">
            {h.serviziPrev.cats.map((c, i) => (
              <Reveal key={c} delay={i * 0.06} className="bg-white p-7 transition-colors duration-300 hover:bg-paper">
                <p className="font-mono text-xs text-accent">{String(i + 1).padStart(2, "0")}</p>
                <p className="mt-3 font-serif text-xl text-ink">{c}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10">
            <Link to="/servizi" data-testid="servizi-preview-cta" className="inline-flex items-center gap-3 rounded-md bg-accent px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-accent-dark">
              {h.serviziPrev.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <FAQ />
      <CTASection />
    </div>
  );
};

export default Home;
